import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { ValueOf } from 'type-fest';

import { pluralizeIssues } from '../../../../../../shared/helpers';
import { isSortSelected, ProjectSortPrefixes } from '../../../../../../shared/projectsV2';
import * as ProjectsV2Store from '../../../../../redux/projectsV2';
import { nuc } from '../../../../../utils';
import { RouterLink } from '../../../../generic';
import Tooltip from '../../../../generic/Tooltip';
import type { Project, ProjectsSort } from '../../../../../../shared/projectsV2';

const SELECTED_CLASS = 'project-item__issue-counts_selected';

type Props = {
  project: Project;
};

const ProjectIssueTypes = {
  Licensing: 'licensing',
  Security: 'security',
  SecurityV2: 'vulnerability',
  Quality: 'quality',
  Total: 'total',
} as const;

type ProjectIssueType = typeof ProjectIssueTypes[keyof typeof ProjectIssueTypes];

const ProjectIssueCounts: VoidFunctionComponent<Props> = ({ project }: Props) => {
  const sort: ProjectsSort = useSelector(ProjectsV2Store.selectors.getSort);

  let activeType: ProjectIssueType = ProjectIssueTypes.Total;
  let issueNavType: ValueOf<typeof ProjectIssueTypes> = ProjectIssueTypes.Licensing;

  if (isSortSelected(sort, ProjectSortPrefixes.Licensing)) {
    activeType = ProjectIssueTypes.Licensing;
  } else if (isSortSelected(sort, ProjectSortPrefixes.Security)) {
    activeType = ProjectIssueTypes.Security;
    issueNavType = ProjectIssueTypes.SecurityV2;
  } else if (isSortSelected(sort, ProjectSortPrefixes.Quality)) {
    activeType = ProjectIssueTypes.Quality;
    issueNavType = ProjectIssueTypes.Quality;
  }

  // Determine which issue count grouping should be selected
  const baseProjectUrl = `/projects/${nuc(project.id)}`;
  let issueUrl: string;
  if (project.version === null) {
    issueUrl = baseProjectUrl;
  } else {
    const baseIssuesUrl = `${baseProjectUrl}/refs/branch/${nuc(project.branch)}/${nuc(project.version)}/issues`;
    issueUrl = activeType === ProjectIssueTypes.Total ? baseProjectUrl : `${baseIssuesUrl}/${issueNavType}`;
  }

  const issuesTotal = project.issues[activeType] ?? '--';

  const unscanned = 'Unscanned';
  const issuesTooltip = project.issues.total !== null
    ? `${issuesTotal} ${activeType !== ProjectIssueTypes.Total ? activeType : ''} ${pluralizeIssues(issuesTotal)}`
    : unscanned;

  return (
    <div className="project-item__issue-counts">
      <Tooltip id="total-issues-tooltip" tooltip={issuesTooltip}>
        <RouterLink className={SELECTED_CLASS} to={issueUrl}>
          {issuesTooltip}
        </RouterLink>
      </Tooltip>
    </div>
  );
};

export default ProjectIssueCounts;
